import { getLocationCoordinates } from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/utils/DeviceMapUtils';
import { calculateDistanceInMiles } from 'utils/maps/geometry.utils';
import type { SimRouting, SimUpstreamLink } from '../FloodSimSettings.types';

/**
 * floodSimRouting.utils — link Stage devices along a waterway, and work out
 * when the flood reaches each.
 *
 * Mike, 2026-08-28: "simulate a flood heading down stream from one to another,
 * and add a time till the down stream node should fire based on a simple
 * distance and speed calculation." Later the same night: "make the down
 * stream devices be able to accept multiple upstream or vice versa, a many to
 * many relationship, some may feed in to one or split in to many".
 *
 * THE MODEL: every device on a waterway runs the SAME scenario, later. The
 * simulator (StageFloodSim_UDT `chainWalk`) walks each device's upstream LINKS
 * to the head(s), summing `travel_minutes` along each path, and runs the
 * device on a head's clock minus that sum. A device may have SEVERAL upstream
 * links — a confluence — and several devices may share one upstream — a fork.
 * At a confluence THE EARLIEST WAVE STARTS THE GAUGE. So THE LINKS ARE THE
 * TRUTH: `routing.upstreams[]`, each `{ uid, travel_minutes, distance_miles,
 * flow_speed_mph }`; `upstream_uid`, `chain`, `position`, `lag_minutes` are
 * derived from them for readers that know only one chain.
 *
 *   travel minutes  = straight-line miles between the two devices ÷ flow speed × 60
 *   lag_minutes     = the smallest sum of travel minutes over the paths to a head
 *
 * "Simple" is the point: a great-circle line between the two pins, not the
 * river's course. A real river is longer, so the flood arrives a little later
 * than this says; the speed input is where an operator corrects for it.
 *
 * Pure. Nothing here reads the store or the network.
 */

/** A river's typical speed. Flash floods in steep channels run faster. */
export const DEFAULT_FLOW_SPEED_MPH = 3;

/** Guards, not limits anyone reaches. */
const MAX_CHAIN_DEPTH = 32;
const MAX_PATHS = 64;

export type DeviceLike = {
  id?: number;
  name?: string;
  alias?: string;
  device_type?: string;
  location?: unknown;
  device_settings?: {
    sim?: {
      routing?: SimRouting | null;
      scenario_name?: string;
      scenario_params?: Record<string, number>;
    } | null;
  } | null;
};

/** A device by id — the device list, with the open form's device overriding its saved row. */
export type DeviceLookup = (id: number) => DeviceLike | undefined;

export const isStageDevice = (device: DeviceLike | null | undefined): boolean =>
  device?.device_type === 'Stage';

/** The routing a device already carries, from the device list's `device_settings.sim`. */
export const routingOf = (device: DeviceLike | null | undefined): SimRouting | null =>
  device?.device_settings?.sim?.routing ?? null;

export const deviceLabel = (device: DeviceLike | null | undefined): string =>
  device?.alias || device?.name || (device?.id != null ? `device ${device.id}` : 'device');

/** Great-circle miles between two devices' pins, or null when either has no coordinates. */
export function deviceDistanceMiles(a: DeviceLike | null | undefined, b: DeviceLike | null | undefined): number | null {
  const ca = getLocationCoordinates(a?.location);
  const cb = getLocationCoordinates(b?.location);
  if (!ca || !cb) return null;
  return calculateDistanceInMiles(ca[0], ca[1], cb[0], cb[1]);
}

/** distance ÷ speed, in minutes. Null when either input cannot make a time. */
export function travelMinutes(distanceMiles: number | null, speedMph: number): number | null {
  if (distanceMiles == null || !(distanceMiles >= 0) || !(speedMph > 0)) return null;
  return (distanceMiles / speedMph) * 60;
}

export const round1 = (n: number): number => Math.round(n * 10) / 10;

// ── The links ───────────────────────────────────────────────────────────────

export interface ResolvedLink {
  uid: number;
  travelMinutes: number;
  distanceMiles: number | null;
  flowSpeedMph: number | null;
}

/** The minutes an OLD single-link row stores: its own hop, or the difference of cumulative lags. */
function legacyHopMinutes(routing: SimRouting, upstreamRouting: SimRouting | null): number {
  if (routing.travel_minutes != null && routing.travel_minutes >= 0) return routing.travel_minutes;
  if (routing.lag_minutes != null) return Math.max(0, routing.lag_minutes - (upstreamRouting?.lag_minutes ?? 0));
  return 0;
}

/**
 * The upstream links of a device: `upstreams[]` when the row has them (each
 * link its own hop), else the single `upstream_uid` with the figures stored
 * beside it. Empty for a head or an unrouted device.
 */
export function upstreamLinksOf(device: DeviceLike | null | undefined, lookup?: DeviceLookup): ResolvedLink[] {
  const r = routingOf(device);
  if (!r) return [];
  if (Array.isArray(r.upstreams)) {
    return r.upstreams
      .filter((l) => l && Number.isInteger(l.uid))
      .map((l) => ({
        uid: l.uid,
        travelMinutes: l.travel_minutes != null && l.travel_minutes >= 0 ? l.travel_minutes : 0,
        distanceMiles: l.distance_miles ?? null,
        flowSpeedMph: l.flow_speed_mph ?? null,
      }));
  }
  if (r.upstream_uid == null) return [];
  const up = lookup ? lookup(r.upstream_uid) : undefined;
  return [{
    uid: r.upstream_uid,
    travelMinutes: legacyHopMinutes(r, routingOf(up)),
    distanceMiles: r.distance_miles ?? null,
    flowSpeedMph: r.flow_speed_mph ?? null,
  }];
}

// ── The walk ────────────────────────────────────────────────────────────────

export interface ChainHop {
  device: DeviceLike;
  /** Minutes from the device above to this one. */
  travelMinutes: number;
}

/** One path from a head down to the device. */
export interface ChainWalk {
  /** The head of the chain when `complete`; otherwise the last device the walk could reach. */
  head: DeviceLike;
  /** Head to `device`, excluding the head, each with its travel time. */
  hops: ChainHop[];
  /** The sum of the hops — the device's cumulative lag from `head`. */
  lagMinutes: number;
  /** 1 = the head. */
  position: number;
  /** False when a linked device is missing from the list or the links loop. */
  complete: boolean;
}

/**
 * Every path from `device` up its links to a head. A confluence gives one
 * path per tributary. A head, or an unrouted device, is one path of no hops.
 */
export function walkUpstreamPaths(device: DeviceLike, lookup: DeviceLookup): ChainWalk[] {
  const paths: ChainWalk[] = [];
  const finish = (head: DeviceLike, hops: ChainHop[], complete: boolean) => {
    if (paths.length >= MAX_PATHS) return;
    paths.push({ head, hops, lagMinutes: hops.reduce((s, h) => s + h.travelMinutes, 0), position: hops.length + 1, complete });
  };
  const stack: Array<{ current: DeviceLike; hops: ChainHop[]; visited: Set<number> }> = [
    { current: device, hops: [], visited: new Set(device.id != null ? [device.id] : []) },
  ];
  while (stack.length > 0 && paths.length < MAX_PATHS) {
    const { current, hops, visited } = stack.pop() as { current: DeviceLike; hops: ChainHop[]; visited: Set<number> };
    const links = upstreamLinksOf(current, lookup);
    if (links.length === 0) {
      finish(current, hops, true);
      continue;
    }
    if (hops.length >= MAX_CHAIN_DEPTH) {
      finish(current, hops, false);
      continue;
    }
    for (const link of [...links].reverse()) {
      const hop: ChainHop = { device: current, travelMinutes: link.travelMinutes };
      const up = lookup(link.uid);
      if (!up || up.id == null || visited.has(up.id)) {
        finish(current, [hop, ...hops], false);
        continue;
      }
      stack.push({ current: up, hops: [hop, ...hops], visited: new Set([...visited, up.id]) });
    }
  }
  return paths;
}

/**
 * The single path callers that know one chain expect: the EARLIEST complete
 * path (the smallest lag — the wave that arrives first), else the first found.
 */
export function walkUpstream(device: DeviceLike, lookup: DeviceLookup): ChainWalk {
  const paths = walkUpstreamPaths(device, lookup);
  const complete = paths.filter((p) => p.complete);
  const pick = complete.length > 0 ? complete : paths;
  return pick.reduce((best, p) => (p.lagMinutes < best.lagMinutes ? p : best), pick[0]);
}

/** The devices linked directly below `device` — more than one when the water forks. */
export function downstreamOf(device: DeviceLike | null | undefined, devices: DeviceLike[]): DeviceLike[] {
  if (device?.id == null) return [];
  return devices.filter((d) => d.id !== device.id && upstreamLinksOf(d).some((l) => l.uid === device.id));
}

/** Every device below `device` at any depth — what must not be offered as its upstream (a loop). */
export function descendantsOf(device: DeviceLike | null | undefined, devices: DeviceLike[]): Set<number> {
  const seen = new Set<number>();
  if (device?.id == null) return seen;
  const queue: DeviceLike[] = [device];
  while (queue.length > 0 && seen.size < devices.length) {
    const next = queue.shift() as DeviceLike;
    for (const child of downstreamOf(next, devices)) {
      if (child.id != null && !seen.has(child.id)) {
        seen.add(child.id);
        queue.push(child);
      }
    }
  }
  return seen;
}

/** Every device above `device` on any path — what must not be offered as its downstream (a loop). */
export function ancestorsOf(device: DeviceLike | null | undefined, lookup: DeviceLookup): Set<number> {
  const out = new Set<number>();
  if (!device) return out;
  for (const path of walkUpstreamPaths(device, lookup)) {
    if (path.head.id != null) out.add(path.head.id);
    for (const hop of path.hops) if (hop.device.id != null) out.add(hop.device.id);
  }
  if (device.id != null) out.delete(device.id);
  return out;
}

/** The chain's name: what its head stores, or one named after the head. */
export function chainNameOf(head: DeviceLike): string {
  return routingOf(head)?.chain || `chain-${head.id ?? '?'}`;
}

// ── Building links ──────────────────────────────────────────────────────────

export interface BuiltLink {
  link: SimUpstreamLink;
  distanceMiles: number | null;
  travelMin: number | null;
  /** Set when the travel time could not be computed. */
  warning: string | null;
}

/** One link from `upstream` into `downstream`: the hop's miles, speed and minutes. */
export function buildLink({
  upstream,
  downstream,
  speedMph,
}: {
  upstream: DeviceLike;
  downstream: DeviceLike;
  speedMph: number;
}): BuiltLink {
  const speed = speedMph > 0 ? speedMph : DEFAULT_FLOW_SPEED_MPH;
  const distanceMiles = deviceDistanceMiles(upstream, downstream);
  const travelMin = travelMinutes(distanceMiles, speed);
  const link: SimUpstreamLink = {
    uid: upstream.id as number,
    flow_speed_mph: speed,
    travel_minutes: travelMin == null ? 0 : round1(travelMin),
  };
  if (distanceMiles != null) link.distance_miles = round1(distanceMiles);
  return {
    link,
    distanceMiles,
    travelMin,
    warning:
      distanceMiles == null
        ? 'One of the two devices has no coordinates, so the travel time is 0 — set the device locations, or accept that both fire together.'
        : null,
  };
}

/**
 * The routing block for a device below `links`: the links as the truth, and
 * the derived figures — `upstream_uid` (the first link), `lag_minutes` (the
 * smallest cumulative lag over the paths, the wave that arrives first),
 * `position` (the longest path), `chain` (the earliest path's head), and the
 * first link's hop figures for readers that know only one. Null with no links.
 */
export function routingWithLinks(links: SimUpstreamLink[], lookup: DeviceLookup, previous?: SimRouting | null): SimRouting | null {
  const clean = links.filter((l) => l && Number.isInteger(l.uid));
  if (clean.length === 0) return null;

  let lag = Number.POSITIVE_INFINITY;
  let position = 1;
  let chain: string | null = null;
  for (const link of clean) {
    const up = lookup(link.uid);
    const travel = link.travel_minutes ?? 0;
    // What the upstream already carries: walked when it has links, stored otherwise (an older row).
    const walk = up ? walkUpstream(up, lookup) : null;
    const walked = !!walk && walk.hops.length > 0;
    const upLag = walked ? (walk as ChainWalk).lagMinutes : routingOf(up)?.lag_minutes ?? 0;
    const upPosition = walked ? (walk as ChainWalk).position : routingOf(up)?.position ?? 1;
    const cumulative = upLag + travel;
    if (cumulative < lag) {
      lag = cumulative;
      chain = walked ? chainNameOf((walk as ChainWalk).head) : up ? routingOf(up)?.chain || `chain-${up.id}` : `chain-${link.uid}`;
    }
    position = Math.max(position, upPosition + 1);
  }

  const first = clean[0];
  return {
    ...(previous?.lag_ticks != null ? {} : {}),
    chain: chain ?? `chain-${first.uid}`,
    position,
    upstream_uid: first.uid,
    upstreams: clean.map((l) => ({ ...l })),
    flow_speed_mph: first.flow_speed_mph ?? DEFAULT_FLOW_SPEED_MPH,
    distance_miles: first.distance_miles,
    travel_minutes: first.travel_minutes,
    lag_minutes: round1(Number.isFinite(lag) ? lag : 0),
  };
}

export interface BuiltRouting {
  routing: SimRouting;
  distanceMiles: number | null;
  travelMin: number | null;
  warning: string | null;
}

/**
 * The routing block for `downstream` placed directly below `upstream` as its
 * ONLY link. With a `lookup` the chain, position and cumulative lag come from
 * walking the links above `upstream`; without one, from what `upstream` stores.
 */
export function buildRouting({
  upstream,
  downstream,
  speedMph,
  lookup,
}: {
  upstream: DeviceLike;
  downstream: DeviceLike;
  speedMph: number;
  lookup?: DeviceLookup;
}): BuiltRouting {
  const built = buildLink({ upstream, downstream, speedMph });
  const resolve: DeviceLookup = (id) => lookup?.(id) ?? (id === upstream.id ? upstream : undefined);
  return {
    routing: routingWithLinks([built.link], resolve) as SimRouting,
    distanceMiles: built.distanceMiles,
    travelMin: built.travelMin,
    warning: built.warning,
  };
}

/** The links of a device's routing as they will be written back. */
function linksOf(routing: SimRouting | null | undefined, self: DeviceLike, lookup: DeviceLookup): SimUpstreamLink[] {
  const withRouting: DeviceLike = { ...self, device_settings: { sim: { routing: routing ?? null } } };
  return upstreamLinksOf(withRouting, lookup).map((l) => ({
    uid: l.uid,
    travel_minutes: l.travelMinutes,
    ...(l.distanceMiles != null ? { distance_miles: l.distanceMiles } : {}),
    ...(l.flowSpeedMph != null ? { flow_speed_mph: l.flowSpeedMph } : {}),
  }));
}

/** `routing` with `link` added (or replaced, by uid): a second tributary joins here. */
export function addUpstream(routing: SimRouting | null | undefined, link: SimUpstreamLink, self: DeviceLike, lookup: DeviceLookup): SimRouting | null {
  const links = linksOf(routing, self, lookup).filter((l) => l.uid !== link.uid);
  return routingWithLinks([...links, link], lookup, routing);
}

/** `routing` without the link from `uid`; null when it was the last. */
export function removeUpstream(routing: SimRouting | null | undefined, uid: number, self: DeviceLike, lookup: DeviceLookup): SimRouting | null {
  const links = linksOf(routing, self, lookup).filter((l) => l.uid !== uid);
  return routingWithLinks(links, lookup, routing);
}

/** `routing` with the link from `uid` recomputed at `speedMph`. */
export function setUpstreamSpeed(routing: SimRouting | null | undefined, uid: number, speedMph: number, self: DeviceLike, lookup: DeviceLookup): SimRouting | null {
  const up = lookup(uid);
  if (!up) return routing ?? null;
  const built = buildLink({ upstream: up, downstream: self, speedMph });
  return addUpstream(routing, built.link, self, lookup);
}

// ── Words ───────────────────────────────────────────────────────────────────

/** One line for the card: when this device fires relative to the upstream one and the head. */
export function describeRouting(routing: SimRouting | null | undefined, upstreamName: string): string | null {
  if (!routing || routing.upstream_uid == null) return null;
  const parts: string[] = [];
  if (routing.distance_miles != null) parts.push(`${round1(routing.distance_miles)} mi from ${upstreamName} (straight line)`);
  if (routing.travel_minutes != null) parts.push(`fires about ${Math.round(routing.travel_minutes)} min after ${upstreamName}`);
  if (routing.lag_minutes != null && routing.position != null && routing.position > 2) {
    parts.push(`${Math.round(routing.lag_minutes)} min after the head of the chain`);
  }
  return parts.join(' · ') || `downstream of ${upstreamName}`;
}

/** "position 4 of the chain headed by Vineyards · fires about 17 min after it". */
export function describeWalk(walk: ChainWalk): string {
  if (walk.hops.length === 0) return '';
  const parts = [
    `position ${walk.position}, ${Math.round(walk.lagMinutes)} min after ${deviceLabel(walk.head)} at the head`,
  ];
  if (!walk.complete) parts.push('a linked device above is not in the list, so this is partial');
  return parts.join(' · ');
}

/** "2 tributaries join here — the wave from Grape Creek (3 min) arrives first". */
export function describeConfluence(paths: ChainWalk[]): string | null {
  const real = paths.filter((p) => p.hops.length > 0);
  if (real.length < 2) return null;
  const earliest = real.reduce((a, p) => (p.lagMinutes < a.lagMinutes ? p : a), real[0]);
  return `${real.length} paths join here — the wave from ${deviceLabel(earliest.head)} (${Math.round(earliest.lagMinutes)} min) arrives first and starts this gauge`;
}
